import { AudioOutlined } from "@ant-design/icons";
import { Button, Select, Tooltip } from "antd";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useResolveClient } from "../../hooks/useResolveClient";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { nudgeNoteTypesFor, useTemplateOptions } from "../../hooks/useTemplateOptions";
import { setRecordingDevice } from "../../slices/recordingsSlice";
import { EventType, trackEvent } from "../../utils/analytics";
import { MAX_RECORDING_TIME_SECONDS } from "../../utils/constants";
import { isTooShort } from "../../utils/recording.utils";
import CustomAudioVisualizer from "./CustomAudioVisuazlier";
import styles from "./recordingcard.module.scss";

type QuickStartRecordingCardProps = {
  isPaused: boolean;
  recordingTime: number;
  handlePauseOrEndRecording: () => void;
  handleResumeRecording: () => void;
  /** Stops (if still recording) and submits in one click. */
  handleSubmitAudio: () => void;
  cancelRecording: () => void;
};

const clock = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

/** Quick Start's recording card: who, which template, how long, and one click to submit. */
const QuickStartRecordingCard = ({
  isPaused,
  recordingTime,
  handlePauseOrEndRecording,
  handleResumeRecording,
  handleSubmitAudio,
  cancelRecording,
}: QuickStartRecordingCardProps) => {
  const { isSubmissionInProgress, audioDevices } = useAppSelector((state) => state.appState);
  const { recordingDetail, mediaRecorder, recordingDevice, withHeadphones } = useAppSelector(
    (state) => state.recordings,
  );
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { sessionTerm } = useSpecialityReference();
  const dispatch = useAppDispatch();

  // Resolve the in-progress patient's name even if they're off the loaded page.
  const { name: clientName } = useResolveClient({
    id: recordingDetail?.client_id,
    uuid: recordingDetail?.client_uuid,
    name: recordingDetail?.client_name,
  });

  const noteTypes = React.useMemo(() => nudgeNoteTypesFor(userInfo), [userInfo]);
  const { options } = useTemplateOptions({ noteTypes, galleryOnMount: false });
  const hasTemplate = recordingDetail?.note_type != null;
  const templateName = hasTemplate
    ? options.find((option) => option.value === recordingDetail.note_type)?.searchText ?? null
    : null;

  const handleDraftRecording = () => {
    trackEvent(EventType.SAVE_AS_DRAFT, {
      recordingTime: recordingTime,
    });
    // This is to ensure the event is tracked before the page is reloaded
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  const busy = isPaused && isSubmissionInProgress;
  const title = recordingDetail?.client_id && clientName ? clientName : `Unassigned ${sessionTerm}`;
  const micLabel = recordingDevice?.label || "Default";

  return (
    <div className={styles.RecordingCard} data-testid="active-recording-card">
      <div className={styles.RecordingCard__head}>
        <span
          className={`${styles.RecordingCard__dot} ${isPaused ? styles["RecordingCard__dot--paused"] : ""}`}
          aria-hidden
        />
        <div className={styles.RecordingCard__titles}>
          <div className={styles.RecordingCard__title} title={title}>
            {title}
          </div>
          <div className={styles.RecordingCard__timer}>
            {isPaused ? "Paused · " : ""}
            {clock(recordingTime)} (max: {Math.floor(MAX_RECORDING_TIME_SECONDS / 60)} mins)
          </div>
        </div>
      </div>

      <div className={styles.RecordingCard__template}>
        Template:{" "}
        {hasTemplate ? (
          <span className={styles.RecordingCard__templateName}>{templateName ?? "Selected"}</span>
        ) : (
          <Tooltip title="Pick the template and confirm the details when you submit.">
            <span className={styles.RecordingCard__templatePending}>choose when you submit</span>
          </Tooltip>
        )}
      </div>

      <div className={styles.RecordingCard__wave}>
        {mediaRecorder && !isPaused ? (
          <CustomAudioVisualizer mediaRecorder={mediaRecorder} width={240} height={26} barColor="rgb(0, 201, 80)" />
        ) : (
          <div className={styles.RecordingCard__waveIdle} />
        )}
      </div>

      <Button
        type="primary"
        block
        loading={isSubmissionInProgress}
        disabled={isSubmissionInProgress}
        className={styles.RecordingCard__submit}
        onClick={handleSubmitAudio}
      >
        Submit Audio
      </Button>

      <div className={styles.RecordingCard__actions}>
        <Button size="small" disabled={busy} onClick={isPaused ? handleResumeRecording : handlePauseOrEndRecording}>
          {isPaused ? "Resume" : "Pause"}
        </Button>
        <Button size="small" disabled={busy || isTooShort(recordingTime)} onClick={handleDraftRecording}>
          Save Draft
        </Button>
        <Button size="small" disabled={isSubmissionInProgress} onClick={cancelRecording}>
          Cancel
        </Button>
      </div>

      {audioDevices && audioDevices.length > 0 && (
        <div className={styles.RecordingCard__mic}>
          <AudioOutlined />
          <Select
            size="small"
            variant="borderless"
            className={styles.RecordingCard__micSelect}
            popupMatchSelectWidth={false}
            options={audioDevices.map((device) => ({
              label: device.label || device.deviceId,
              value: device.deviceId,
            }))}
            value={recordingDevice?.deviceId}
            labelRender={() => `Microphone: ${micLabel}`}
            onChange={(value) => {
              const selectedDevice = audioDevices.find((device) => device.deviceId === value);
              if (selectedDevice) {
                dispatch(setRecordingDevice(selectedDevice));
              }
            }}
            disabled={withHeadphones}
          />
        </div>
      )}
    </div>
  );
};

export default QuickStartRecordingCard;
